/**
 * HeadquartersRegisterPage Component
 * VROONG 본사 관리자 회원가입 페이지
 *
 * @description
 * 본사 직원 계정 생성 폼
 * 아이디, 이름, 비밀번호, 휴대폰번호, 이메일(선택) 입력 및 유효성 검증
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastPosition, ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { extractErrorMessage } from '@/shared/utils/errorHandler';
import { UserIcon, EmailIcon, PhoneIcon, LoginIcon } from '@/shared/components/icons/Icons';
import PasswordInput from '@/shared/components/forms/PasswordInput';
import FormInput from '@/shared/components/forms/FormInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import LinkButton from '@/shared/components/buttons/LinkButton';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
    validateUserId,
    validateUserName,
    validatePassword,
    validatePasswordConfirmation,
    validatePhoneNumber,
    validateEmail,
    ValidationErrors,
} from '@/features/auth/lib/validation';
import { HEADQUARTERS_FEATURES } from '@/features/auth/config/feature.config';
import BrandSection from '../../ui/BrandSection/BrandSection';
import VersionInfo from '../../ui/VersionInfo';
import '@/features/auth/styles/auth.css';

// ============================================================
// 상수 정의
// ============================================================

/** 회원가입 성공 후 로그인 페이지 이동 지연 시간 (ms) */
const REDIRECT_DELAY = 1500;

/** 토스트 기본 옵션 */
const TOAST_OPTIONS = {
    position: ToastPosition.TOP_CENTER,
} as const;

/** 도움말 링크 컨테이너 스타일 */
const HELP_LINKS_STYLE: React.CSSProperties = {
    marginBottom: '5rem',
};

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 본사 관리자 회원가입 페이지
 */
const HeadquartersRegisterPage: React.FC = () => {
    // ======== Hooks ========
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { registerHeadquarters, isLoading } = useAuth();

    // ======== State ========
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [phoneNum, setPhoneNum] = useState('');
    const [emailInfo, setEmailInfo] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});

    // ======== Memoized Values ========
    /** 브랜드 섹션 설명 텍스트 */
    const brandDescription = useMemo(() => (
        <>
            VROONG 정산 시스템 본사 관리자로 가입합니다
            <br />
            전체 협력사 및 라이더 정산을 관리하세요
        </>
    ), []);

    // ======== Validation ========
    /**
     * 폼 전체 유효성 검증
     */
    const validateForm = useCallback((): boolean => {
        const errors: ValidationErrors = {};

        const userIdError = validateUserId(userId, 4);
        if (userIdError) errors.userId = userIdError;

        const userNameError = validateUserName(userName, 2);
        if (userNameError) errors.userName = userNameError;

        const passwordError = validatePassword(password);
        if (passwordError) errors.password = passwordError;

        const passwordConfirmError = validatePasswordConfirmation(password, passwordConfirm);
        if (passwordConfirmError) errors.passwordConfirm = passwordConfirmError;

        const phoneNumError = validatePhoneNumber(phoneNum);
        if (phoneNumError) errors.phoneNum = phoneNumError;

        const emailError = validateEmail(emailInfo, false);
        if (emailError) errors.emailInfo = emailError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [userId, userName, password, passwordConfirm, phoneNum, emailInfo]);

    // ======== Event Handlers ========
    /**
     * 아이디 입력 핸들러
     */
    const handleUserIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUserId(e.target.value);
    }, []);

    /**
     * 이름 입력 핸들러
     */
    const handleUserNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(e.target.value);
    }, []);

    /**
     * 비밀번호 입력 핸들러
     */
    const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    }, []);

    /**
     * 비밀번호 확인 입력 핸들러
     */
    const handlePasswordConfirmChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordConfirm(e.target.value);
    }, []);

    /**
     * 휴대폰번호 입력 핸들러 (숫자만 허용)
     */
    const handlePhoneNumChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNum(e.target.value.replace(/[^0-9]/g, ''));
    }, []);

    /**
     * 이메일 입력 핸들러
     */
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInfo(e.target.value);
    }, []);

    /**
     * 회원가입 폼 제출 처리
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        try {
            const response = await registerHeadquarters({
                userId,
                userName,
                password,
                phoneNum,
                emailInfo: emailInfo || undefined,
            });

            if (response.success) {
                showToast('회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.', {
                    type: ToastType.SUCCESS,
                    ...TOAST_OPTIONS,
                });
                setTimeout(() => navigate('/login'), REDIRECT_DELAY);
            } else {
                const errorMsg = response.message || '회원가입에 실패했습니다.';
                showToast(errorMsg, { type: ToastType.ERROR, ...TOAST_OPTIONS });
                setError(errorMsg);
            }
        } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err);
            showToast(errorMessage, { type: ToastType.ERROR, ...TOAST_OPTIONS });
            setError(errorMessage);
        }
    }, [validateForm, registerHeadquarters, userId, userName, password, phoneNum, emailInfo, showToast, navigate]);

    /**
     * 계정 유형 선택 페이지 이동
     */
    const handleNavigateToRegister = useCallback(() => {
        navigate('/register');
    }, [navigate]);

    /**
     * 로그인 페이지 이동
     */
    const handleNavigateToLogin = useCallback(() => {
        navigate('/login');
    }, [navigate]);

    // ======== Render ========
    return (
        <div className="login-page">
            <div className="login-container">
                {/* 좌측 브랜드 섹션 */}
                <BrandSection
                    title="본사 관리자 가입"
                    description={brandDescription}
                    features={HEADQUARTERS_FEATURES}
                />

                {/* 우측 회원가입 폼 섹션 */}
                <div className="login-section">
                    <div className="login-form-container">
                        {/* 헤더 */}
                        <div className="login-header">
                            <h2>본사 관리자 회원가입</h2>
                            <p>정보를 입력하여 계정을 생성하세요</p>
                        </div>

                        {/* 회원가입 폼 */}
                        <form className="login-form" onSubmit={handleSubmit}>
                            {/* 아이디 */}
                            <FormInput
                                id="userId"
                                name="userId"
                                value={userId}
                                onChange={handleUserIdChange}
                                placeholder="아이디를 입력하세요 (4자 이상)"
                                label="아이디"
                                required
                                disabled={isLoading}
                                icon={<UserIcon />}
                                error={fieldErrors.userId}
                            />

                            {/* 이름 */}
                            <FormInput
                                id="userName"
                                name="userName"
                                value={userName}
                                onChange={handleUserNameChange}
                                placeholder="이름을 입력하세요"
                                label="이름"
                                required
                                disabled={isLoading}
                                icon={<UserIcon />}
                                error={fieldErrors.userName}
                            />

                            {/* 비밀번호 */}
                            <PasswordInput
                                id="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="8자 이상, 영문/숫자/특수문자 포함"
                                required
                                disabled={isLoading}
                                error={fieldErrors.password}
                            />

                            {/* 비밀번호 확인 */}
                            <PasswordInput
                                id="passwordConfirm"
                                name="passwordConfirm"
                                value={passwordConfirm}
                                onChange={handlePasswordConfirmChange}
                                placeholder="비밀번호를 다시 입력하세요"
                                label="비밀번호 확인"
                                required
                                disabled={isLoading}
                                error={fieldErrors.passwordConfirm}
                            />

                            {/* 휴대폰번호 */}
                            <FormInput
                                id="phoneNum"
                                name="phoneNum"
                                type="tel"
                                value={phoneNum}
                                onChange={handlePhoneNumChange}
                                placeholder="숫자만 입력 (10~12자리)"
                                label="휴대폰번호"
                                required
                                disabled={isLoading}
                                icon={<PhoneIcon />}
                                error={fieldErrors.phoneNum}
                            />

                            {/* 이메일 (선택) */}
                            <FormInput
                                id="emailInfo"
                                name="emailInfo"
                                type="email"
                                value={emailInfo}
                                onChange={handleEmailChange}
                                placeholder="example@vroong.com (선택사항)"
                                label="이메일"
                                disabled={isLoading}
                                icon={<EmailIcon />}
                                error={fieldErrors.emailInfo}
                            />

                            {/* 에러 메시지 */}
                            {error && <div className="error-message">{error}</div>}

                            <PrimaryButton type="submit" loading={isLoading} disabled={isLoading}>
                                회원가입
                            </PrimaryButton>
                        </form>

                        {/* 추가 옵션 */}
                        <div className="additional-options">
                            <div className="divider">
                                <span>또는</span>
                            </div>

                            <div className="help-links" style={HELP_LINKS_STYLE}>
                                <LinkButton onClick={handleNavigateToRegister}>
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    계정 유형 다시 선택
                                </LinkButton>
                                <LinkButton onClick={handleNavigateToLogin}>
                                    <LoginIcon width={16} height={16} />
                                    로그인 페이지로
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버전 정보 */}
                    <VersionInfo />
                </div>
            </div>
        </div>
    );
};

export default HeadquartersRegisterPage;
